import React from "react";
import { motion } from "framer-motion";

// Importing local images
import gym1 from "../assets/gym1.jpg";
import gym2 from "../assets/gym2.jpg";
import gym3 from "../assets/gym3.jpg";
import gym4 from "../assets/gym4.jpg";

const App = () => {
  // Member data with local image imports
  const members = [
    {
      name: "N. Girish",
      photo: gym1,
      role: "Elite Fitness Mentor & Competition Coach",
      description: "Master trainer specializing in advanced fitness training and strength conditioning with over 10 years of experience.",
      academic: ["Certified Personal Trainer", "Diploma in Sports Science", "Advanced Strength & Conditioning Specialist"],
      specialties: ["💪 Strength Training", "🏆 Competition Prep", "⚡ Performance Enhancement"],
      experience: "10+ Years"
    },
    {
      name: "K. Poojitha",
      photo: gym2,
      role: "Yoga Master & Wellness Specialist",
      description: "Ph.D in Yoga Therapy with expertise in holistic wellness, mindfulness practices, and therapeutic yoga interventions.",
      academic: ["Ph.D in Yoga Therapy", "National Referee - Yoga Federation of India", "Advanced NIS in Yoga", "Certified Wellness Coach"],
      specialties: ["🧘 Therapeutic Yoga", "🌟 Mindfulness Training", "🌿 Holistic Wellness"],
      experience: "15+ Years"
    },
    {
      name: "Rupa Uppada",
      photo: gym4,
      role: "Elite Athlete Coach & Yoga Specialist",
      description: "National-level athlete turned professional trainer, combining elite sports performance training with therapeutic yoga practice for comprehensive fitness development.",
      academic: [
        "2017: Hockey Senior National Participation - Haryana",
        "2017: Athletics U-19 South Nationals & Jr. Nationals",
        "2018: Athletics Jr Nationals 4×400 Relay Bronze Medal",
        "2019: Hockey All India University - Chennai",
        "2022: Athletics All India University Cross Country - Karnataka",
        "Certified Yoga Instructor",
        "Sports Performance Specialist"
      ],
      specialties: ["🏃 Athletic Training", "🥅 Sports Performance", "🧘 Yoga Therapy", "🏅 Competition Coaching"],
      experience: "10+ Years"
    },
    {
      name: "Chaitanya",
      photo: gym3,
      role: "Fitness Coach & Nutrition Expert",
      description: "Dynamic fitness coach combining cutting-edge training methodologies with comprehensive nutritional guidance for optimal results.",
      academic: ["Certified Fitness Trainer", "Sports Nutrition Specialist", "Body Transformation Expert"],
      specialties: ["🔥 HIT Training", "🥗 Nutrition Planning", "📈 Body Transformation"],
      experience: "8+ Years"
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white overflow-x-hidden">
      
      {/* Hero Section */}
      <motion.section 
        className="relative py-16 lg:py-24 bg-gradient-to-br from-black via-gray-900 to-gray-800"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#fded22]/5 to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-7xl font-black mb-4 lg:mb-6 tracking-tight"
            variants={itemVariants}
            whileHover={{ 
              scale: 1.02, 
              color: "#fded22",
              textShadow: "0px 0px 30px rgba(253,237,34,0.8)"
            }}
            transition={{ duration: 0.3 }}
          >
            MEET YOUR <span className="text-[#fded22]">TRAINERS</span>
          </motion.h1>
          <motion.div 
            className="h-1 w-32 lg:w-48 bg-gradient-to-r from-[#fded22] to-yellow-500 mx-auto mb-6 lg:mb-8"
            initial={{ width: 0 }}
            animate={{ width: "auto" }}
            transition={{ delay: 0.5, duration: 0.8 }}
          />
          <motion.p 
            className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
            Under the guidance of our elite team of certified professionals who are dedicated to your transformation journey
          </motion.p>
        </div>
      </motion.section>

      {/* Trainers Section */}
      <motion.section 
        className="py-16 lg:py-24"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 lg:space-y-24">
            {members.map((member, index) => (
              <motion.div
                key={index}
                className="relative"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={containerVariants}
              >
                {/* Desktop Layout */}
                <div className={`hidden lg:flex items-center gap-12 xl:gap-16 ${
                  index % 2 === 0 ? '' : 'flex-row-reverse'
                }`}>
                  
                  {/* Image Section */}
                  <motion.div 
                    className="flex-shrink-0"
                    variants={index % 2 === 0 ? slideInLeft : slideInRight}
                  >
                    <motion.div 
                      className="relative group"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.img
                src={member.photo}
                alt={member.name}
                        className="w-80 h-80 xl:w-96 xl:h-96 rounded-3xl object-cover shadow-2xl border-4 border-gray-700 group-hover:border-[#fded22] transition-all duration-300"
                        variants={imageVariants}
                        whileHover={{ 
                          borderColor: "#fded22",
                          boxShadow: "0px 0px 40px rgba(253,237,34,0.4)"
                        }}
                      />
                      <motion.div
                        className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#fded22]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      />
                      
                      {/* Experience Badge */}
                      <motion.div 
                        className="absolute -top-4 -right-4 bg-[#fded22] text-black px-4 py-2 rounded-full font-black text-sm shadow-lg"
                        initial={{ scale: 0, rotate: -10 }}
                        whileInView={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                      >
                        {member.experience}
                      </motion.div>
                    </motion.div>
                  </motion.div>

                  {/* Content Section */}
                  <motion.div 
                    className="flex-1 space-y-6"
                    variants={index % 2 === 0 ? slideInRight : slideInLeft}
                  >
                    <motion.div variants={cardVariants}>
                      <motion.h2 
                        className="text-4xl xl:text-5xl font-black mb-3 text-white"
                        whileHover={{ 
                          color: "#fded22",
                          scale: 1.02
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {member.name}
                      </motion.h2>
                      <motion.p 
                        className="text-xl xl:text-2xl font-bold text-[#fded22] mb-4"
                        whileHover={{ scale: 1.02 }}
                      >
                        {member.role}
                      </motion.p>
                      <motion.p 
                        className="text-lg xl:text-xl text-gray-300 leading-relaxed mb-6"
                        whileHover={{ color: "#d1d5db" }}
                      >
                        {member.description}
                      </motion.p>
                    </motion.div>

                    {/* Specialties */}
                    <motion.div 
                      className="space-y-4"
                      variants={cardVariants}
                    >
                      <motion.h4 
                        className="text-xl font-black text-[#fded22] mb-3"
                        whileHover={{ scale: 1.05 }}
                      >
                        SPECIALTIES
                      </motion.h4>
                      <motion.div className="grid grid-cols-1 gap-2">
                        {member.specialties.map((specialty, idx) => (
                          <motion.div 
                            key={idx}
                            className="flex items-center text-gray-300 group cursor-pointer"
                            whileHover={{ 
                              x: 10,
                              color: "#fded22"
                            }}
                            transition={{ duration: 0.2 }}
                          >
                            <motion.span 
                              className="text-2xl mr-3"
                              whileHover={{ scale: 1.3, rotate: 10 }}
                              transition={{ duration: 0.3 }}
                            >
                              {specialty.split(' ')[0]}
                            </motion.span>
                            <span className="font-semibold">{specialty.substring(2)}</span>
                          </motion.div>
                        ))}
                      </motion.div>
                    </motion.div>

                    {/* Academic Credentials/Achievements */}
                    <motion.div 
                      className="space-y-4"
                      variants={cardVariants}
                    >
                      <motion.h4 
                        className="text-xl font-black text-[#fded22] mb-3"
                        whileHover={{ scale: 1.05 }}
                      >
                        {member.name === "Rupa Uppada" ? "ACHIEVEMENTS & CREDENTIALS" : "CREDENTIALS"}
                      </motion.h4>
                      <motion.div className="space-y-2 max-h-60 overflow-y-auto overflow-x-hidden">
                        {member.academic.map((item, idx) => (
                          <motion.div 
                            key={idx}
                            className="flex items-center text-gray-300 group cursor-pointer"
                            whileHover={{ 
                              x: 10,
                              color: "#fded22"
                            }}
                            transition={{ duration: 0.2 }}
                          >
                            <motion.div
                              className="w-3 h-3 bg-[#fded22] rounded-full mr-3 mt-1 flex-shrink-0"
                              whileHover={{ scale: 1.5 }}
                              transition={{ duration: 0.3 }}
                            />
                            <span className="font-medium text-sm xl:text-base">{item}</span>
                          </motion.div>
                        ))}
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </div>

                {/* Mobile/Tablet Layout */}
                <div className="lg:hidden">
                  <motion.div 
                    className="text-center mb-8"
                    variants={cardVariants}
                  >
                    <motion.div 
                      className="relative inline-block mb-6"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.img
                        src={member.photo}
                        alt={member.name}
                        className="w-48 h-48 sm:w-64 sm:h-64 rounded-3xl object-cover shadow-2xl border-4 border-gray-700 mx-auto"
                        variants={imageVariants}
                        whileHover={{ 
                          borderColor: "#fded22",
                          boxShadow: "0px 0px 30px rgba(253,237,34,0.4)"
                        }}
                      />
                      <motion.div 
                        className="absolute -top-3 -right-3 bg-[#fded22] text-black px-3 py-1 rounded-full font-black text-xs"
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ delay: 0.5, type: "spring" }}
                      >
                        {member.experience}
                      </motion.div>
                    </motion.div>

                    <motion.h2 
                      className="text-3xl sm:text-4xl font-black mb-2 text-white"
                      whileHover={{ color: "#fded22", scale: 1.02 }}
                    >
                {member.name}
                    </motion.h2>
                    <motion.p 
                      className="text-lg sm:text-xl font-bold text-[#fded22] mb-4"
                      whileHover={{ scale: 1.02 }}
                    >
                {member.role}
                    </motion.p>
                    <motion.p 
                      className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 px-4"
                      whileHover={{ color: "#d1d5db" }}
                    >
                {member.description}
                    </motion.p>
                  </motion.div>

                  {/* Mobile Specialties & Credentials */}
                  <motion.div 
                    className="grid grid-cols-1 sm:grid-cols-2 gap-6"
                    variants={containerVariants}
                  >
                    <motion.div variants={cardVariants}>
                      <motion.h4 
                        className="text-lg font-black text-[#fded22] mb-3 text-center"
                        whileHover={{ scale: 1.05 }}
                      >
                        SPECIALTIES
                      </motion.h4>
                      <div className="space-y-2">
                        {member.specialties.map((specialty, idx) => (
                          <motion.div 
                            key={idx}
                            className="flex items-center justify-center text-gray-300 text-sm"
                            whileHover={{ color: "#fded22" }}
                          >
                            <span className="mr-2">{specialty.split(' ')[0]}</span>
                            <span>{specialty.substring(2)}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>

                    <motion.div variants={cardVariants} className="max-w-full overflow-hidden">
                      <motion.h4 
                        className="text-base sm:text-lg font-black text-[#fded22] mb-3 text-center"
                        whileHover={{ scale: 1.05 }}
                      >
                        {member.name === "Rupa Uppada" ? "ACHIEVEMENTS" : "CREDENTIALS"}
                      </motion.h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto overflow-x-hidden max-w-full">
                        {member.academic.slice(0, 3).map((item, idx) => (
                          <motion.div 
                            key={idx}
                            className="flex items-center justify-center text-gray-300 text-xs text-center"
                            whileHover={{ color: "#fded22" }}
                          >
                            <span className="break-words text-center px-2">{item}</span>
                          </motion.div>
                        ))}
                        {member.academic.length > 3 && (
                          <motion.p 
                            className="text-[#fded22] text-xs text-center mt-2"
                            whileHover={{ scale: 1.05 }}
                          >
                            +{member.academic.length - 3} more achievements
                          </motion.p>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                </div>

                {/* Separator Line */}
                {index < members.length - 1 && (
                  <motion.div 
                    className="mt-16 lg:mt-20 h-px bg-gradient-to-r from-transparent via-[#fded22] to-transparent"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Call to Action Section */}
      <motion.section 
        className="py-16 lg:py-20 bg-gradient-to-r from-[#fded22] to-yellow-500"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        variants={containerVariants}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 
            className="text-4xl lg:text-6xl font-black text-black mb-6"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            TRAIN WITH THE BEST
          </motion.h2>
          <motion.p 
            className="text-xl lg:text-2xl text-gray-800 mb-8 max-w-3xl mx-auto"
            variants={itemVariants}
          >
            Experience personalized training with our certified professionals and achieve results you never thought possible
          </motion.p>
          {/* <motion.button 
            className="bg-black text-[#fded22] px-12 py-4 lg:px-16 lg:py-6 rounded-full text-xl lg:text-2xl font-black uppercase tracking-wider shadow-2xl"
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05, 
              boxShadow: "0px 15px 40px rgba(0,0,0,0.3)",
              y: -5
            }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            BOOK A SESSION
          </motion.button> */}
        </div>
      </motion.section>

      {/* Floating Elements Animation - Gym themed */}
      <motion.div
        className="fixed inset-0 pointer-events-none overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
      >
        {['💪', '🏋️', '🔥', '⚡', '🏆', '💯'].map((icon, i) => (
          <motion.div
            key={i}
            className="absolute text-2xl opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              rotate: [0, 10, -10, 0],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          >
            {icon}
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default App;
